import { MapPin, Phone, Mail, Linkedin, Instagram, Send, MessageCircle, Users, Code2, Lightbulb, Heart } from "lucide-react";
import teamImg from "@/assets/about-team.jpg";
import torontoImg from "@/assets/about-toronto.jpg";
import collabImg from "@/assets/about-collaboration.jpg";

// X (Twitter) icon — lucide doesn't ship a current X glyph
const XIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M18.244 2H21.5l-7.51 8.583L22.75 22h-6.93l-5.42-7.094L4.2 22H.94l8.04-9.19L1.25 2h7.1l4.9 6.48L18.244 2zm-1.215 18h1.92L7.06 4H5.01l12.02 16z" />
  </svg>
);

// WhatsApp icon
const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.05 21.785h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884a9.825 9.825 0 016.99 2.898 9.825 9.825 0 012.892 6.992c-.003 5.45-4.437 9.886-9.886 9.886zm8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892a11.86 11.86 0 001.587 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const socials = [
  { name: "LinkedIn", href: "https://linkedin.com", Icon: Linkedin, hover: "hover:bg-[#0A66C2] hover:text-white hover:border-[#0A66C2]" },
  { name: "X (Twitter)", href: "https://x.com", Icon: XIcon, hover: "hover:bg-foreground hover:text-background hover:border-foreground" },
  { name: "Instagram", href: "https://instagram.com", Icon: Instagram, hover: "hover:bg-gradient-to-tr hover:from-[#feda75] hover:via-[#d62976] hover:to-[#4f5bd5] hover:text-white hover:border-transparent" },
  { name: "Telegram", href: "https://t.me", Icon: Send, hover: "hover:bg-[#229ED9] hover:text-white hover:border-[#229ED9]" },
  { name: "WhatsApp", href: "https://wa.me/16478602524", Icon: WhatsAppIcon, hover: "hover:bg-[#25D366] hover:text-white hover:border-[#25D366]" },
];

const pillars = [
  {
    Icon: Users,
    title: "Global Experts",
    text: "Seasoned analysts and market specialists from across the world.",
  },
  {
    Icon: Code2,
    title: "Engineering Team",
    text: "Large dedicated developer teams continuously evolving the platform.",
  },
  {
    Icon: Lightbulb,
    title: "Open to Feedback",
    text: "We embrace every suggestion that helps traders use JouRex better.",
  },
];

export const AboutSection = () => {
  return (
    <section id="about" className="container mx-auto px-4 sm:px-6 lg:px-8 mt-20 lg:mt-28 mb-20 lg:mb-28">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-amber-50 via-card to-orange-50/40 ring-1 ring-amber-200/60 shadow-card">
        {/* Decorative blobs */}
        <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-amber-300/30 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-orange-300/20 blur-3xl" />

        <div className="relative p-8 lg:p-14">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-10">
            <div className="inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-3.5 py-1.5 mb-4">
              <Heart className="h-3.5 w-3.5 text-amber-700" />
              <span className="text-[11px] font-bold tracking-widest text-amber-800 uppercase">
                About Us
              </span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold tracking-tight text-foreground">
              Meet the family behind JouRex.
            </h2>
            <p className="mt-4 text-base lg:text-lg text-muted-foreground leading-relaxed">
              JouRex is built by a global family of financial market experts, quants and engineers — collaborating
              across continents to give traders an honest edge. We listen, iterate, and welcome every piece of
              feedback that makes the platform sharper for the people who rely on it.
            </p>
          </div>

          {/* Inner card */}
          <div className="rounded-2xl overflow-hidden bg-card shadow-elevated ring-1 ring-border/50">
            {/* Image strip */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-1 bg-border/50">
              <div className="relative aspect-[4/3] md:aspect-auto md:h-64 lg:h-72 overflow-hidden bg-muted group">
                <img
                  src={teamImg}
                  alt="The JouRex team of analysts and developers"
                  loading="lazy"
                  width={1280}
                  height={832}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                <div className="absolute bottom-3 left-4 text-white">
                  <p className="text-[10px] font-bold tracking-widest uppercase opacity-80">Our Team</p>
                  <p className="text-sm font-semibold">Analysts &amp; Engineers</p>
                </div>
              </div>
              <div className="relative aspect-[4/3] md:aspect-auto md:h-64 lg:h-72 overflow-hidden bg-muted group">
                <img
                  src={collabImg}
                  alt="JouRex collaboration and craftsmanship"
                  loading="lazy"
                  width={1280}
                  height={832}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                <div className="absolute bottom-3 left-4 text-white">
                  <p className="text-[10px] font-bold tracking-widest uppercase opacity-80">Our Craft</p>
                  <p className="text-sm font-semibold">Built With Care</p>
                </div>
              </div>
              <div className="relative aspect-[4/3] md:aspect-auto md:h-64 lg:h-72 overflow-hidden bg-muted group">
                <img
                  src={torontoImg}
                  alt="Toronto, Canada — JouRex headquarters"
                  loading="lazy"
                  width={1280}
                  height={832}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                <div className="absolute bottom-3 left-4 text-white">
                  <p className="text-[10px] font-bold tracking-widest uppercase opacity-80">Headquarters</p>
                  <p className="text-sm font-semibold">Toronto, Canada</p>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
              {/* Story + pillars */}
              <div className="lg:col-span-3 p-6 sm:p-8 lg:p-10 border-b lg:border-b-0 lg:border-r border-border/60">
                <h3 className="text-xl lg:text-2xl font-bold text-foreground mb-3">
                  A family of market experts and engineers.
                </h3>
                <p className="text-sm lg:text-base text-muted-foreground leading-relaxed">
                  JouRex brings together a worldwide group of seasoned market analysts and financial specialists,
                  backed by large engineering teams that build, refine and scale the platform every single day. Our
                  mission is to translate professional-grade trading discipline into tools that any serious trader
                  can actually use.
                </p>
                <p className="mt-3 text-sm lg:text-base text-muted-foreground leading-relaxed">
                  We are deeply community-driven. JouRex welcomes every comment, critique and idea — and channels
                  them into features that genuinely improve the trader's day-to-day workflow. If it helps you trade
                  better, we want to hear about it.
                </p>

                <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {pillars.map((p) => (
                    <div
                      key={p.title}
                      className="rounded-xl border border-border/60 bg-background/60 p-4 hover:border-amber-300/70 hover:shadow-card transition-all"
                    >
                      <div className="h-9 w-9 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center mb-2">
                        <p.Icon className="h-4.5 w-4.5" />
                      </div>
                      <p className="text-sm font-bold text-foreground leading-tight">{p.title}</p>
                      <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{p.text}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Contact + socials */}
              <div className="lg:col-span-2 p-6 sm:p-8 lg:p-10 bg-gradient-to-br from-amber-50/60 via-background to-orange-100/30">
                <h3 className="text-xl lg:text-2xl font-bold text-foreground mb-5">Get in touch</h3>

                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 h-9 w-9 shrink-0 rounded-lg bg-card ring-1 ring-border/60 flex items-center justify-center text-amber-700">
                      <MapPin className="h-4.5 w-4.5" />
                    </div>
                    <div>
                      <p className="text-[11px] font-bold tracking-widest text-muted-foreground uppercase">Address</p>
                      <p className="text-sm font-semibold text-foreground leading-snug">
                        Don Mills Road, North York
                        <br />
                        Toronto, Ontario, Canada
                      </p>
                    </div>
                  </div>

                  <a
                    href="tel:+16478602524"
                    className="flex items-start gap-3 group"
                  >
                    <div className="mt-0.5 h-9 w-9 shrink-0 rounded-lg bg-card ring-1 ring-border/60 flex items-center justify-center text-amber-700 group-hover:bg-amber-600 group-hover:text-white group-hover:ring-amber-600 transition-colors">
                      <Phone className="h-4.5 w-4.5" />
                    </div>
                    <div>
                      <p className="text-[11px] font-bold tracking-widest text-muted-foreground uppercase">Phone</p>
                      <p className="text-sm font-semibold text-foreground group-hover:text-amber-700 transition-colors">
                        +1 (647) 860-2524
                      </p>
                    </div>
                  </a>

                  <a
                    href="mailto:hello@jourex.trade"
                    className="flex items-start gap-3 group"
                  >
                    <div className="mt-0.5 h-9 w-9 shrink-0 rounded-lg bg-card ring-1 ring-border/60 flex items-center justify-center text-amber-700 group-hover:bg-amber-600 group-hover:text-white group-hover:ring-amber-600 transition-colors">
                      <Mail className="h-4.5 w-4.5" />
                    </div>
                    <div>
                      <p className="text-[11px] font-bold tracking-widest text-muted-foreground uppercase">Email</p>
                      <p className="text-sm font-semibold text-foreground group-hover:text-amber-700 transition-colors">
                        hello@jourex.trade
                      </p>
                    </div>
                  </a>
                </div>

                <div className="mt-6 pt-5 border-t border-border/60">
                  <p className="text-[11px] font-bold tracking-widest text-muted-foreground uppercase mb-3">
                    Follow us
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {socials.map(({ name, href, Icon, hover }) => (
                      <a
                        key={name}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={name}
                        title={name}
                        className={`h-10 w-10 rounded-lg border border-border bg-card text-foreground/70 flex items-center justify-center transition-all hover:-translate-y-0.5 ${hover}`}
                      >
                        <Icon className="h-4.5 w-4.5" />
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
